import React, {useRef, useState} from 'react';
import {classNames} from '@utils/class-names';
import ArticleStatusIndicator from "./ui/article-status-indicator";
import {ArticleSearchResultDto} from "../../core/models/interfaces/article-search-result-dto.interface";
import ArticleTag from "./ui/article-tag";
import {ReactComponent as DotsIcon} from "@assets/icons/dots.svg";
import { ReactComponent as ShareIcon } from "@assets/icons/share.svg";
import { ReactComponent as QrCodeIcon } from "@assets/icons/qr-code.svg";
import Popover, {PopoverHandle} from "@common/components/popover";
import ArticleActions from "./article-actions";
import {useNavigate} from "react-router-dom";
import {UniversalClickHandler} from "@common/models/types/universal-click-handler.type";
import SelectedCheckmark from "./ui/selected-checkmark";
import {stripHtmlToText} from "../helpers/strip-html-to-text";
import {DateHelper} from "@utils/date-helper";
import {useArticleApi} from "../../core/hooks/use-article-api";
import {convertLexicalJsonToHtml} from "../helpers/lexical-to-html";
import {ArticleDetails} from "../../core/models/interfaces/article-details.interface";
import {convertLexicalJsonToPlainText} from "../helpers/lexical-to-plain-text";
import sanitizeUrlString from "@utils/sanitize-url-string";
import useModalWithData from "@common/hooks/use-modal-with-data";
import {ArticleQrInfoModalData} from "./article-qr-info-modal";
import {ModalIds} from "@common/models/enums/modal-ids.enum";
import {ShareArticleModalData} from "./share-article-modal";
import AppButton from "@common/components/app-button";

interface ArticleCardProps {
    article: ArticleSearchResultDto;
    className?: string;
    onClick?: UniversalClickHandler<void, ArticleSearchResultDto>;
    showActions?: boolean;
    isSelected?: boolean;
}

const ArticleCard: React.FC<ArticleCardProps> = (props: ArticleCardProps) => {
    const {
        article,
        className = '',
        showActions = true,
        onClick,
        isSelected = false,
    } = props;
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const navigate = useNavigate();
    const clickEditArticle = () => navigate(`./edit/${article.id}`)
    const {getArticle} = useArticleApi();
    const popoverRef = useRef<PopoverHandle>(null);
    const { showModal: showQrModal } = useModalWithData<ArticleQrInfoModalData>(ModalIds.ARTICLE_QR_INFO);
    const { showModal: showShareModal } = useModalWithData<ShareArticleModalData>(ModalIds.SHARE_ARTICLE);

    const onClickHandler = () => onClick ? onClick(article) : clickEditArticle()

    const clickQrCodeArticleHandler = async (e: any): Promise<void> => {
        e.stopPropagation();
        popoverRef.current?.close()
        const data: ArticleDetails | undefined = getArticle && await getArticle(article.id);
        const plainTextTitle = convertLexicalJsonToPlainText(data?.title || "");
        const shareUrl = `${window.location.origin}/article/${data!.appId}/${sanitizeUrlString(plainTextTitle)}`;
        showQrModal({
            title: data?.title || "",
            url: shareUrl,
        });
    }

    const clickShareArticleHandler = async (e : any): Promise<void> => {
        e.stopPropagation();
        const data: ArticleDetails | undefined = await getArticle(article.id);
        popoverRef.current?.close()
        const plainTextTitle = convertLexicalJsonToPlainText(data?.title || "");
        const shareUrl = `${window.location.origin}/article/${data!.appId}/${sanitizeUrlString(plainTextTitle)}`;
        showShareModal({
            articleId: data?.id,
            articleTitle: data?.title,
            shareUrl,
        });
    }

    return (
        <div
            className={classNames(
                'group rounded-2xl p-2 cursor-pointer transition-all duration-200',
                isSelected && article.isVisible && "bg-success/10",
                isSelected && !article.isVisible && "bg-error/10",
                !isSelected && !showActions && "hover:bg-input-background/5",
                !isPopoverOpen && !isSelected && "hover:bg-input-background/5",
                isPopoverOpen && 'bg-input-background/5',
                "flex justify-between items-center",
                className
            )}
            onClick={onClickHandler}
        >
            <div>
                <h3 className="font-semibold text-base">{stripHtmlToText(convertLexicalJsonToHtml(article.title))}</h3>
                <div>
                    <div className="inline-block">
                        <ArticleStatusIndicator isVisible={article.isVisible}/>
                    </div>
                    {article.isUniversal && (
                        <span className="text-xs ml-2 font-semibold text-primary bg-primary/10 px-2 py-1 rounded-full">
                            Uniwersalny
                        </span>
                    )}
                    {article.isPostoperative && (
                        <span className="text-xs ml-2 font-semibold text-primary bg-primary/10 px-2 py-1 rounded-full">
                            Uniwersalny pozabiegowy
                        </span>
                    )}
                    {article.tags && article.tags.length > 0 && (
                        <div className="inline-block align-middle text-sm text-on-surface/60 w-fit">
                            {article.tags.map((tag, index) => (
                                <React.Fragment key={index}>
                                    <span className="mx-2 text-[8px] bottom-[1px] relative">•</span>
                                    <ArticleTag text={tag}/>
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                    {article.edited && (
                        <div className="inline-block align-middle text-sm text-on-surface/60 w-fit">
                            <span className="mx-2 text-[8px] bottom-[1px] relative">•</span>
                            <span>Aktualizowano {DateHelper.formatDate(new Date(article.edited), "DD.MM.YYYY")}</span>
                        </div>
                    )}
                </div>
            </div>
            {isSelected && (
                <div className={classNames(article.isVisible ? "bg-success" : "bg-error", "rounded-full")}>
                    <SelectedCheckmark/>
                </div>
            )}
            {showActions && (
                <div className={classNames(
                    "transition-opacity duration-100 flex",
                    isPopoverOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                )}>
                    <div className="h-8 w-8 flex items-center justify-center hover:bg-input-background/5 rounded-full">
                        <AppButton
                            onClick={clickQrCodeArticleHandler}
                            className="!p-0 !border-0 !rounded-none"
                            classNameContentWrapper="!block w-full">
                                <QrCodeIcon />
                        </AppButton>
                    </div>
                    <div className="h-8 w-8 flex items-center justify-center hover:bg-input-background/5 rounded-full">
                        <AppButton
                            onClick={clickShareArticleHandler}
                            className="!p-0 !border-0 !rounded-none"
                            classNameContentWrapper="!block w-full">
                                <ShareIcon />
                        </AppButton>
                    </div>
                    <Popover
                        ref={popoverRef}
                        content={
                            <ArticleActions
                                canDelete={!article.isUniversal && !article.isPostoperative}
                                onClosePopover={popoverRef.current?.close}
                                getArticleData={getArticle.bind(this, article.id)}
                                articleId={article.id}
                                isUniversalArticle={article.isUniversal || article.isPostoperative}
                                isVisibleArticle={article.isVisible}
                                showToggleVisibilityAction={true}
                                showSetUniversalAction={!article.isUniversal && !article.isPostoperative && article.isVisible}
                                showSetPostoperativeUniversalAction={!article.isPostoperative && !article.isUniversal && article.isVisible}
                                showEditAction={true}
                            />
                        }
                        onOpen={() => setIsPopoverOpen(true)}
                        onClose={() => setIsPopoverOpen(false)}
                    >
                        <div className={classNames(
                            "h-8 w-8 flex items-center justify-center rounded-full",
                            isPopoverOpen ? "bg-input-background/10" : "hover:bg-input-background/5"
                        )}>
                            <DotsIcon/>
                        </div>
                    </Popover>
                </div>
            )}
        </div>
    );
};

export default ArticleCard;
